import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/client/components/ui/input';
import { cn } from '@/client/lib/utils';
import { PersonRow } from './PersonRow';
import type { RouterOutputs } from '@/client/utils/trpc';

type Employee = RouterOutputs['employees']['list'][number];

interface PeopleListProps {
  people: Employee[];
  isLoading: boolean;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}

export function PeopleList({ people, isLoading, selectedId, onSelect }: PeopleListProps) {
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');

  const depts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of people) {
      const dept = p.job_title?.split(' ')[0] ?? 'Other';
      counts.set(dept, (counts.get(dept) ?? 0) + 1);
    }
    return counts;
  }, [people]);

  const filtered = useMemo(() => {
    return people.filter((p) => {
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        (p.full_name?.toLowerCase().includes(q) ?? false) ||
        p.email.toLowerCase().includes(q) ||
        (p.job_title?.toLowerCase().includes(q) ?? false);
      const matchesDept = deptFilter === 'All' || p.job_title?.split(' ')[0] === deptFilter;
      return matchesSearch && matchesDept;
    });
  }, [people, search, deptFilter]);

  return (
    <div className="flex w-80 shrink-0 flex-col overflow-hidden border-r">
      <div className="flex flex-col gap-2.5 border-b p-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="h-8 pl-8 text-sm"
            placeholder={`Search ${people.length} people…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterBadge
            label={`All · ${people.length}`}
            active={deptFilter === 'All'}
            onClick={() => setDeptFilter('All')}
          />
          {Array.from(depts.entries()).map(([dept, count]) => (
            <FilterBadge
              key={dept}
              label={`${dept} · ${count}`}
              active={deptFilter === dept}
              onClick={() => setDeptFilter(dept)}
            />
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">No people found.</p>
        )}
        {filtered.map((p) => (
          <PersonRow
            key={p.id}
            person={p}
            isSelected={p.id === selectedId}
            onClick={() => onSelect(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function FilterBadge({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        active
          ? 'border-foreground bg-foreground text-background'
          : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}
